import React, { useEffect, useState } from 'react';
import { Form, Select, message } from 'antd';
import Cookies from 'js-cookie';
import axios from '../../backendService';

const ScheduleLectureForm = ({ setFormdata }) => {
  const [form] = Form.useForm();
  const userId = Cookies.get('userId');

  // States for API data
  const [courseData, setCourseData] = useState([]);
  const [batchData, setBatchData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [chapterData, setChapterData] = useState([]);
  const [topicData, setTopicData] = useState([]);

  // States for selected values
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedbatch, setSelectedBatch] = useState('');
  const [selectedTopics, setSelectedTopics] = useState('');

  const [isBatchDisabled, setIsBatchDisabled] = useState(true);
  const [isSubjectDisabled, setIsSubjectDisabled] = useState(true);
  const [isChapterDisabled, setIsChapterDisabled] = useState(true);
  const [isTopicDisabled, setIsTopicDisabled] = useState(true);

  //Fetch Into new API

  // === Fetch Courses (Initial Load) ===
  const fetchCourses = async () => {
    try {
      const response = await axios.post('/batch/getByTeacher', { teacherId: userId });
      // console.log('response--------', response);
      if (response.data.success) {
        message.success('Courses loaded successfully');
        setCourseData(response.data.data);
      } else {
        message.error(response.data.message || 'Failed to load courses.');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      message.error('Error fetching courses. Please try again.');
    }
  };

  // === Fetch Batches ===
  const fetchBatches = async (courseId) => {
    try {
      const response = await axios.post('/batch/getFilteredBatches', { teacherId: userId, courseId });
      if (response.data.success) {
        // console.log('----------------------', response.data.data);
        message.success('Batch loaded successfully');

        setBatchData(response.data.data);
        setIsBatchDisabled(false);
      } else {
        setBatchData([]);
        setIsBatchDisabled(true);
        message.error(response.data.message || 'No batches found for the selected course.');
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      message.error('Error fetching batches. Please try again.');
    }
  };

  // === Fetch Subjects ===
  const fetchSubjects = async (courseId) => {
    try {
      const response = await axios.post('/courses/getMatchedSubjects', { courseId, teacherId: userId });
      if (response.data.success) {
        message.success('Subjects loaded successfully');

        setSubjectData(response.data.data);
        setIsSubjectDisabled(false);
      } else {
        setSubjectData([]);
        setIsSubjectDisabled(true);
        message.error(response.data.message || 'No subjects found for the selected course.');
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      message.error('Error fetching subjects. Please try again.');
    }
  };

  // === Fetch Chapters ===
  const fetchChapters = async (subjectId) => {
    try {
      const response = await axios.post('/chapters/getChaptersBySubject', { subjectId });
      if (response.data.success) {
        message.success('Chapters loaded successfully');

        setChapterData(response.data.data);
        setIsChapterDisabled(false);
      } else {
        setChapterData([]);
        setIsChapterDisabled(true);
        message.error(response.data.message || 'No chapters found for the selected subject.');
      }
    } catch (error) {
      console.error('Error fetching chapters:', error);
      message.error('Error fetching chapters. Please try again.');
    }
  };

  // === Fetch Topics ===
  const fetchTopics = async (chapterId) => {
    try {
      const response = await axios.post('/chapters/getTopicsByChapter', { chapterId });
      if (response.data.success) {
        message.success('Topics loaded successfully');

        setTopicData(response.data.data);
        setIsTopicDisabled(false);
      } else {
        setTopicData([]);
        setIsTopicDisabled(true);
        message.error(response.data.message || 'No topics found for the selected chapter.');
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
      message.error('Error fetching topics. Please try again.');
    }
  };

  // === useEffect: Fetch Courses on Mount ===
  useEffect(() => {
    fetchCourses();
    form.resetFields();
  }, []);

  // === useEffect: Fetch Batches and Subjects when Course changes ===
  useEffect(() => {
    if (selectedCourse) {
      fetchBatches(selectedCourse);
      fetchSubjects(selectedCourse);

      // Reset downstream dependencies
      setBatchData([]);
      setSubjectData([]);
      setChapterData([]);
      setTopicData([]);
      setIsBatchDisabled(true);
      setIsSubjectDisabled(true);
      setIsChapterDisabled(true);
      setIsTopicDisabled(true);
    }
  }, [selectedCourse]);

  // === useEffect: Fetch Chapters when Subject changes ===
  useEffect(() => {
    if (selectedSubject) {
      fetchChapters(selectedSubject);

      // Reset downstream dependencies
      setChapterData([]);
      setTopicData([]);
      setIsChapterDisabled(true);
      setIsTopicDisabled(true);
    }
  }, [selectedSubject]);

  // === useEffect: Fetch Topics when Chapter changes ===
  useEffect(() => {
    if (selectedChapter) {
      fetchTopics(selectedChapter);

      // Reset downstream dependencies
      setTopicData([]);
      setIsTopicDisabled(true);
    }
  }, [selectedChapter]);
  // console.log('selectedbatch', selectedbatch);
  useEffect(() => {
    setFormdata({ selectedCourse, selectedSubject, selectedChapter, selectedbatch, selectedTopics });
  }, [selectedCourse, selectedSubject, selectedChapter, selectedbatch, selectedTopics]);
  return (
    <>
      <Form layout={'vertical'} form={form}>
        {/* Course Selection */}
        <Form.Item label="Course" name="course" rules={[{ required: true, message: 'Please select a course!' }]}>
          <Select
            placeholder="Select a course"
            onChange={(value) => {
              setSelectedCourse(value);
              form.resetFields(['batches', 'subjectName', 'chapterName', 'topicName']);
            }}
          >
            {courseData.map((course) => (
              <Select.Option key={course.courseId} value={course.courseId}>
                {course.courseName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Batch Selection */}
        <Form.Item label="Batches" name="batches" rules={[{ required: true, message: 'Please select a batch!' }]}>
          <Select
            mode="multiple"
            placeholder="Select batches"
            onChange={(value) => {
              setSelectedBatch(value);
              form.resetFields(['subjectName', 'chapterName', 'topicName']);
            }}
            disabled={isBatchDisabled}
          >
            {batchData.map((batch) => (
              <Select.Option key={batch.batchId} value={batch.batchId}>
                {batch.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Subject Selection */}
        <Form.Item label="Subject" name="subjectName" rules={[{ required: true, message: 'Please select a subject!' }]}>
          <Select
            placeholder="Select a subject"
            onChange={(value) => {
              setSelectedSubject(value);
              form.resetFields(['chapterName', 'topicName']);
            }}
            disabled={isSubjectDisabled}
          >
            {subjectData.map((subject) => (
              <Select.Option key={subject.subjectId} value={subject.subjectId}>
                {subject.subjectName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {/* Chapter Selection */}
        <Form.Item label="Chapter" name="chapterName" rules={[{ required: true, message: 'Please select a chapter!' }]}>
          <Select
            placeholder="Select a chapter"
            onChange={(value) => {
              setSelectedChapter(value);
              form.resetFields(['topicName']);
            }}
            disabled={isChapterDisabled}
          >
            {chapterData.map((chapter) => (
              <Select.Option key={chapter.chapterId} value={chapter.chapterId}>
                {chapter.chapterName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {/* Topic Selection */}
        <Form.Item label="Topic" name="topicName" rules={[{ required: true, message: 'Please select a topic!' }]}>
          <Select
            placeholder="Select a topic"
            onChange={(value) => {
              setSelectedTopics(value);
              // form.resetFields(['topicName']);
            }}
            disabled={isTopicDisabled}
          >
            {topicData.map((topic) => (
              <Select.Option key={topic.topicId} value={topic.topicId}>
                {topic.topicName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      {/* {showFormSubmitBar && <Progress percent={formSubmitPercentage} className=" z-99998" />} */}
    </>
  );
};

export default ScheduleLectureForm;
